"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Search, Star } from "lucide-react"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { getAvailableGames, getLocalizedGameId } from "@/lib/games-data"
import { useTranslation } from "@/hooks/use-translation"
import { useFavorites } from "@/context/favorites-context"

// Simplified search - show all games directly without categories, favorites first

export function CommandSearch() {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const navigate = useNavigate()
  const { t, language } = useTranslation()
  const { isFavorite } = useFavorites()

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [])

  const availableGames = getAvailableGames(language)

  // Filtrar juegos por término de búsqueda
  const filteredGames = searchTerm.length > 0
    ? availableGames.filter(game =>
      t(`games.${game.id}.name`).toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.tags.some(tag => t(`tags.${tag}`).toLowerCase().includes(searchTerm.toLowerCase()))
    )
    : availableGames

  // Ordenar juegos: favoritos primero, luego el resto
  const sortedGames = [...filteredGames].sort((a, b) => {
    const aIsFavorite = isFavorite(a.id)
    const bIsFavorite = isFavorite(b.id)
    if (aIsFavorite && !bIsFavorite) return -1
    if (!aIsFavorite && bIsFavorite) return 1
    return 0
  })

  const handleGameSelect = (gameId: string) => {
    const localizedId = getLocalizedGameId(gameId, language)
    navigate(`/game/${localizedId}`)
    setOpen(false)
    setSearchTerm("")
  }

  return (
    <>
      <Button
        variant="outline"
        className="w-full justify-start text-muted-foreground font-normal"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        {t("search.placeholder")}
        <kbd className="pointer-events-none ml-auto inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog open={open} onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (!isOpen) {
          setSearchTerm("")
        }
      }}>
        <CommandInput
          placeholder={t("search.searchPlaceholder")}
          value={searchTerm}
          onValueChange={setSearchTerm}
        />
        <CommandList>
          <CommandEmpty>{t("search.noResults")}</CommandEmpty>

          {/* All Games */}
          <CommandGroup>
            {sortedGames.map((game) => {
              const gameIsFavorite = isFavorite(game.id)
              return (
                <CommandItem
                  key={game.id}
                  onSelect={() => handleGameSelect(game.id)}
                  className={gameIsFavorite ? "bg-yellow-50 dark:bg-yellow-900/20" : ""}
                >
                  <game.icon className="mr-2 h-4 w-4" />
                  <span className={gameIsFavorite ? "font-semibold" : ""}>
                    {t(`games.${game.id}.name`)}
                  </span>
                  {gameIsFavorite && (
                    <Star className="ml-2 h-3 w-3 fill-yellow-400 text-yellow-400" />
                  )}
                  <div className="ml-auto flex gap-1">
                    {game.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {t(`tags.${tag}`)}
                      </Badge>
                    ))}
                  </div>
                </CommandItem>
              )
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  )
} 